import { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Filter, X, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSm, ScrollText } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/DateInput';
import { LockedFeatureCard, SectionLoadingCard } from '@/components/LockedFeatureCard';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantPlan } from '@/hooks/useTenantPlan';
import api from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';

// Maps action keys to human labels + an icon-color class. Add new entries
// here as new audit-log actions are introduced; unknown actions fall back
// to the raw key.
const ACTION_LABELS: Record<string, { label: string; tone: string }> = {
  'user.created':              { label: 'User invited',           tone: 'text-emerald-600 bg-emerald-50' },
  'user.updated':              { label: 'User updated',           tone: 'text-slate-600 bg-slate-50' },
  'user.role_changed':         { label: 'User role changed',      tone: 'text-amber-600 bg-amber-50' },
  'user.activated':            { label: 'User activated',         tone: 'text-emerald-600 bg-emerald-50' },
  'user.deactivated':          { label: 'User deactivated',       tone: 'text-red-600 bg-red-50' },
  'user.deleted':              { label: 'User deleted',           tone: 'text-red-600 bg-red-50' },
  'user.password_reset_by_admin': { label: 'Password reset (admin)', tone: 'text-amber-600 bg-amber-50' },
  'role.created':              { label: 'Role created',           tone: 'text-emerald-600 bg-emerald-50' },
  'role.updated':              { label: 'Role updated',           tone: 'text-slate-600 bg-slate-50' },
  'role.activated':            { label: 'Role activated',         tone: 'text-emerald-600 bg-emerald-50' },
  'role.deactivated':          { label: 'Role deactivated',       tone: 'text-red-600 bg-red-50' },
  'role.deleted':              { label: 'Role deleted',           tone: 'text-red-600 bg-red-50' },
  'brand.updated':             { label: 'Brand settings updated', tone: 'text-slate-600 bg-slate-50' },
  'integration.updated':       { label: 'Integration updated',    tone: 'text-indigo-600 bg-indigo-50' },
  'tenant.auto_merge_toggled': { label: 'Auto-merge toggled',     tone: 'text-indigo-600 bg-indigo-50' },
};

interface AuditLogEntry {
  id: string;
  action: string;
  actorId: string | null;
  actorEmail: string | null;
  entityType: string | null;
  entityId: string | null;
  changes: any;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  actor: { id: string; name: string; email: string } | null;
}

interface AuditLogResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function AuditLogPage() {
  const { user } = useAuth();
  const isPrivileged = user?.role === 'admin' || user?.role === 'manager';
  const { hasFeature, planName, loading: planLoading } = useTenantPlan();
  const hasAuditAccess = hasFeature('audit_log');

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', dateFrom: '', dateTo: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 25 };
      if (filters.action) params.action = filters.action;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const { data } = await api.get<AuditLogResponse>('/audit-log', { params });
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch {
      // server-level access errors are surfaced via Sentry; the toast/UI
      // intentionally stays quiet — empty state already conveys "nothing
      // visible to you" without leaking the reason.
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // Skip the gated GET if the tenant's plan doesn't include audit_log.
  // Otherwise we'd 402 on every Audit Log mount and pop the upgrade modal
  // unprompted — the inline LockedFeatureCard tells the same story without
  // hijacking the screen.
  useEffect(() => {
    if (!isPrivileged || !hasAuditAccess) return;
    fetchLogs();
  }, [fetchLogs, isPrivileged, hasAuditAccess]);

  if (!isPrivileged) {
    return (
      <DashboardLayout title="Audit Log">
        <Card><CardContent className="p-6">
          <p className="text-sm text-slate-500">Only admins and managers can view the audit log.</p>
        </CardContent></Card>
      </DashboardLayout>
    );
  }

  if (planLoading) {
    return (
      <DashboardLayout title="Audit Log">
        <SectionLoadingCard />
      </DashboardLayout>
    );
  }

  if (!hasAuditAccess) {
    return (
      <DashboardLayout title="Audit Log">
        <LockedFeatureCard
          icon={<ScrollText className="h-5 w-5 text-[#6366F1]" />}
          title="Audit Log"
          description="A tamper-evident record of every sensitive admin action: user invites, role changes, permission grants, billing changes, and integration edits — with actor, IP, and full diff."
          planName={planName}
          requiredTier="Growth"
        />
      </DashboardLayout>
    );
  }

  const clearFilters = () => setFilters({ action: '', dateFrom: '', dateTo: '' });
  const hasFilters = filters.action || filters.dateFrom || filters.dateTo;

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  return (
    <DashboardLayout title="Audit Log">
      <div className="space-y-4">
        {/* Filter bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(v => !v)}
                className={hasFilters ? 'border-[#6366F1] text-[#6366F1]' : ''}
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filters {hasFilters && <span className="ml-1 text-xs">(active)</span>}
              </Button>
              <p className="text-xs text-slate-500 ml-auto">{total.toLocaleString()} entries</p>
            </div>
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
                <div>
                  <label className="text-xs font-normal text-slate-600">Action type</label>
                  <Input
                    placeholder="user.created, role., brand."
                    value={filters.action}
                    onChange={e => { setPage(1); setFilters(f => ({ ...f, action: e.target.value })); }}
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">Tip: end with a dot to filter all actions in a domain (e.g. user.)</p>
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600">From</label>
                  <DateInput
                    value={filters.dateFrom}
                    onChange={v => { setPage(1); setFilters(f => ({ ...f, dateFrom: v })); }}
                  />
                </div>
                <div>
                  <label className="text-xs font-normal text-slate-600">To</label>
                  <DateInput
                    value={filters.dateTo}
                    onChange={v => { setPage(1); setFilters(f => ({ ...f, dateTo: v })); }}
                  />
                </div>
                {hasFilters && (
                  <button
                    onClick={() => { clearFilters(); setPage(1); }}
                    className="text-xs text-[#6366F1] hover:underline col-span-full text-left flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Clear filters
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-20">
                <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-600 font-medium">No audit entries</p>
                <p className="text-xs text-slate-400 mt-1">
                  {hasFilters ? 'Try adjusting your filters' : 'Sensitive actions will be logged here'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {logs.map(log => {
                  const meta = ACTION_LABELS[log.action] ?? { label: log.action, tone: 'text-slate-600 bg-slate-50' };
                  const isOpen = expanded.has(log.id);
                  return (
                    <div key={log.id} className="px-4 py-3 hover:bg-slate-50/50">
                      <button
                        onClick={() => toggleExpand(log.id)}
                        className="w-full flex items-start gap-3 text-left"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {isOpen
                            ? <ChevronDown className="h-4 w-4 text-slate-400" />
                            : <ChevronRightSm className="h-4 w-4 text-slate-400" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs text-slate-400 font-mono">{log.action}</span>
                            {log.entityType && log.entityId && (
                              <span className="text-[10px] text-slate-400">
                                {log.entityType}:{log.entityId.slice(-6)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-1">
                            <span className="font-medium">{log.actor?.name ?? log.actorEmail ?? 'Unknown'}</span>
                            <span className="text-slate-400"> · {formatDateTime(log.createdAt)}</span>
                            {log.ip && <span className="text-slate-400"> · {log.ip}</span>}
                          </p>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="ml-7 mt-2 bg-slate-50 rounded p-2.5 text-xs space-y-1">
                          {log.changes && Object.keys(log.changes).length > 0 ? (
                            <pre className="whitespace-pre-wrap break-all text-slate-600 font-mono">
                              {JSON.stringify(log.changes, null, 2)}
                            </pre>
                          ) : (
                            <p className="text-slate-400 italic">No additional details</p>
                          )}
                          {log.userAgent && (
                            <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-200 mt-1.5">
                              {log.userAgent}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-2">
            <p className="text-xs text-slate-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
